import VueWrapper from '@/components/core/Vue/vue.wrapper';
import BaseFormComponent from '@/components/vuetify/Form/base-form';
import {TerminalBlockDataModel, TerminalBlockModel, TerminalBlockSaveDto, TerminalBlockTableCellDto} from '@/sdk';
import {TerminalBlockApi} from '@/sdk/api-services';
import {Component, Prop} from 'vue-property-decorator';

interface DynamicTableData {
    currentValues: TerminalBlockDataModel[];
    crossSectionValues: TerminalBlockDataModel[];
    tableData: {[key: string]: string}[];
    matrix: {[currentId: string]: {[crossSectionId: string]: string}};
}

interface TableHeader {
    text: string;
    value: string;
    sortable: boolean;
    width?: string;
    colspan?: number;
    rowspan?: number;
    isSubHeader?: boolean;
}

@Component({
    name: 'SaveTerminalBlockDialogComponent',
})
export default class SaveTerminalBlockDialogComponent extends VueWrapper {
    public readonly name: string = 'save-terminal-block-dialog';

    public $refs!: {
        baseFormRef: BaseFormComponent;
    };

    // API instance
    private terminalBlockApi: TerminalBlockApi = new TerminalBlockApi();

    @Prop({
        type: String,
        default: null,
    })
    public readonly terminalBlockId!: string | null;

    @Prop({
        type: String,
        required: true,
    })
    public readonly locationId!: string;

    public terminalBlockForm: TerminalBlockModel = new TerminalBlockModel();

    // Dynamic table properties
    public dynamicTable: DynamicTableData = {
        currentValues: [],
        crossSectionValues: [],
        tableData: [],
        matrix: {},
    };

    public newCurrentValue: string = '';
    public newCrossSectionValue: string = '';

    public created() {
        // Ensure locationId is always set
        if (!this.terminalBlockForm.locationId) {
            this.terminalBlockForm.locationId = this.locationId;
        }
        this.sortCurrentValues();
        this.sortCrossSectionValues();
        this.generateTableData();
    }

    public async save() {
        if (this.$refs.baseFormRef.validate()) {
            this.LoaderSrv.showFullScreenLoader();

            try {
                // Prepare the terminal block data
                const terminalBlockData: TerminalBlockSaveDto = {
                    id: this.terminalBlockForm.id || undefined,
                    locationId: this.terminalBlockForm.locationId || this.locationId,
                    manufacturer: this.terminalBlockForm.manufacturer || '',
                    modelNumber: this.terminalBlockForm.modelNumber || '',
                    serialNumber: this.terminalBlockForm.serialNumber || '',
                    exMarking: this.terminalBlockForm.exMarking || '',
                    certificationNumber: this.terminalBlockForm.certificationNumber || '',
                    ratedMaximumDissipatedPower:
                        this.terminalBlockForm.ratedMaximumDissipatedPower != null ? Number(this.terminalBlockForm.ratedMaximumDissipatedPower) : null,
                };

                // Save the terminal block first
                const savedTerminalBlock = await this.terminalBlockApi.save(terminalBlockData);

                if (savedTerminalBlock && savedTerminalBlock.id) {
                    // Update the form with the saved terminal block
                    this.terminalBlockForm = savedTerminalBlock;

                    // Save all table cell data using addIntersectionValue
                    await this.saveAllTableCellData(savedTerminalBlock.id);

                    this.$emit('saved', savedTerminalBlock);
                    this.closeDialog();
                    this.LoaderSrv.hideFullScreenLoader();
                } else {
                    throw new Error('Failed to save terminal block');
                }
            } catch (error) {
                console.error('Error saving terminal block:', error);
                this.LoaderSrv.hideFullScreenLoader();
                // TODO: Show error message to user
            }
        }
    }

    private async saveAllTableCellData(terminalBlockId: string) {
        const tableCellsToSave: TerminalBlockTableCellDto[] = [];

        // Collect all non-empty table cell data
        this.dynamicTable.tableData.forEach((row, rowIndex) => {
            const currentValue = this.dynamicTable.currentValues[rowIndex];

            if (currentValue && currentValue.id) {
                this.dynamicTable.crossSectionValues.forEach((crossSection, colIndex) => {
                    if (crossSection && crossSection.id && currentValue.id && crossSection.id) {
                        const cellKey = `cross_${colIndex}`;
                        const cellValue = row[cellKey];

                        if (cellValue && cellValue.trim()) {
                            tableCellsToSave.push({
                                currentId: currentValue.id,
                                crossSectionId: crossSection.id,
                                value: cellValue.trim(),
                            });
                        }
                    }
                });
            }
        });

        // Save all table cells if there are any
        if (tableCellsToSave.length > 0) {
            await this.terminalBlockApi.addIntersectionValue(terminalBlockId, tableCellsToSave);
        }
    }

    public reset() {
        this.terminalBlockForm = new TerminalBlockModel();
        this.terminalBlockForm.locationId = this.locationId; // Ensure locationId is always set
        this.dynamicTable = {
            currentValues: [],
            crossSectionValues: [],
            tableData: [],
            matrix: {},
        };
        this.sortCurrentValues();
        this.sortCrossSectionValues();
        this.generateTableData();
        this.$refs.baseFormRef?.reset();
    }

    public async openDialog() {
        console.log('Opening SaveTerminalBlockDialogComponent with ID:', this.terminalBlockId);

        // Always reset first to ensure clean state
        this.reset();

        if (this.terminalBlockId) {
            await this.loadTerminalBlockData(this.terminalBlockId);
        }

        this.CoreSrv.OpenModal(this.name);
    }

    public closeDialog() {
        this.CoreSrv.CloseModal(this.name);
    }

    private async loadTerminalBlockData(terminalBlockId: string) {
        try {
            this.LoaderSrv.showFullScreenLoader();

            // Get the full terminal block data with structured response
            const terminalBlockData = await this.terminalBlockApi.getById(terminalBlockId);

            if (terminalBlockData) {
                // Store the loaded terminal block
                this.terminalBlockForm = terminalBlockData.terminalBlock || new TerminalBlockModel();

                // Load current values
                this.dynamicTable.currentValues = terminalBlockData.currents || [];

                // Load cross-section values
                this.dynamicTable.crossSectionValues = terminalBlockData.crossSections || [];

                // Load matrix data if available
                if (terminalBlockData.matrix) {
                    this.dynamicTable.matrix = terminalBlockData.matrix;
                }

                // Sort the values and regenerate table
                this.sortCurrentValues();
                this.sortCrossSectionValues();
                this.generateTableData();

                // Populate matrix data into table
                this.populateMatrixDataIntoTable(terminalBlockData.matrix);
            }
        } catch (error) {
            console.error('Error loading terminal block data:', error);
            this.dynamicTable = {
                currentValues: [],
                crossSectionValues: [],
                tableData: [],
                matrix: {},
            };
        } finally {
            this.LoaderSrv.hideFullScreenLoader();
        }
    }

    private populateMatrixDataIntoTable(matrixData?: {[currentId: string]: {[crossSectionId: string]: string}}) {
        if (!matrixData || !this.dynamicTable.tableData.length) return;

        // Populate the table data with matrix values
        this.dynamicTable.tableData.forEach((row, rowIndex) => {
            const currentValue = this.dynamicTable.currentValues[rowIndex];
            if (currentValue && currentValue.id) {
                this.dynamicTable.crossSectionValues.forEach((crossSection, colIndex) => {
                    if (crossSection && crossSection.id && currentValue.id && crossSection.id) {
                        const cellKey = `cross_${colIndex}`;
                        // Check if matrix data exists for this current and cross-section combination
                        if (matrixData[currentValue.id] && matrixData[currentValue.id][crossSection.id]) {
                            row[cellKey] = matrixData[currentValue.id][crossSection.id];
                        }
                    }
                });
            }
        });
    }

    // Dynamic table methods
    public async addCurrentRow() {
        if (this.newCurrentValue.trim()) {
            // First ensure we have a terminal block saved
            if (!this.terminalBlockForm.id) {
                await this.saveTerminalBlockIfNeeded();
            }

            if (this.terminalBlockForm.id) {
                try {
                    this.LoaderSrv.showFullScreenLoader();

                    // Call API to add current value
                    await this.terminalBlockApi.addCurrent(this.terminalBlockForm.id, {
                        value: this.newCurrentValue.trim(),
                    });

                    // Clear the input
                    this.newCurrentValue = '';

                    // Reload terminal block data to sync with server
                    await this.loadTerminalBlockData(this.terminalBlockForm.id);

                    this.LoaderSrv.hideFullScreenLoader();
                } catch (error) {
                    console.error('Error adding current value:', error);
                    this.LoaderSrv.hideFullScreenLoader();
                    // TODO: Show error message to user
                }
            }
        }
    }

    private async saveTerminalBlockIfNeeded() {
        if (!this.terminalBlockForm.id) {
            const terminalBlockData: TerminalBlockSaveDto = {
                locationId: this.terminalBlockForm.locationId || this.locationId,
                manufacturer: this.terminalBlockForm.manufacturer || '',
                modelNumber: this.terminalBlockForm.modelNumber || '',
                serialNumber: this.terminalBlockForm.serialNumber || '',
                exMarking: this.terminalBlockForm.exMarking || '',
                certificationNumber: this.terminalBlockForm.certificationNumber || '',
                ratedMaximumDissipatedPower:
                    this.terminalBlockForm.ratedMaximumDissipatedPower != null ? Number(this.terminalBlockForm.ratedMaximumDissipatedPower) : null,
            };

            const savedTerminalBlock = await this.terminalBlockApi.save(terminalBlockData);
            if (savedTerminalBlock) {
                this.terminalBlockForm = savedTerminalBlock;
            }
        }
    }

    public async removeCurrentRow(index: number) {
        const currentValue = this.dynamicTable.currentValues[index];
        const message = `Are you sure you want to remove the current value "${
            currentValue.value || ''
        }"?\n\nThis will delete all data in this row and cannot be undone.`;

        this.ConfirmSrv.open('Remove Current Row', message).then(async confirmed => {
            if (confirmed) {
                try {
                    this.LoaderSrv.showFullScreenLoader();

                    // Call API to delete current value if it has an ID
                    if (currentValue.id) {
                        await this.terminalBlockApi.deleteData(currentValue.id);
                    }

                    // Reload terminal block data to sync with server
                    if (this.terminalBlockForm.id) {
                        await this.loadTerminalBlockData(this.terminalBlockForm.id);
                    }

                    this.LoaderSrv.hideFullScreenLoader();
                } catch (error) {
                    console.error('Error removing current value:', error);
                    this.LoaderSrv.hideFullScreenLoader();
                    // TODO: Show error message to user
                }
            }
        });
    }

    public async addCrossSectionColumn() {
        if (this.newCrossSectionValue.trim()) {
            // First ensure we have a terminal block saved
            if (!this.terminalBlockForm.id) {
                await this.saveTerminalBlockIfNeeded();
            }

            if (this.terminalBlockForm.id) {
                try {
                    this.LoaderSrv.showFullScreenLoader();

                    // Call API to add cross-section value
                    await this.terminalBlockApi.addCrossSection(this.terminalBlockForm.id, {
                        value: this.newCrossSectionValue.trim(),
                    });

                    // Clear the input
                    this.newCrossSectionValue = '';

                    // Reload terminal block data to sync with server
                    await this.loadTerminalBlockData(this.terminalBlockForm.id);

                    this.LoaderSrv.hideFullScreenLoader();
                } catch (error) {
                    console.error('Error adding cross-section value:', error);
                    this.LoaderSrv.hideFullScreenLoader();
                    // TODO: Show error message to user
                }
            }
        }
    }

    public async removeCrossSectionColumn(index: number) {
        const crossSectionValue = this.dynamicTable.crossSectionValues[index];
        const message = `Are you sure you want to remove the cross section value "${
            crossSectionValue.value || ''
        }"?\n\nThis will delete all data in this column and cannot be undone.`;

        this.ConfirmSrv.open('Remove Cross Section Column', message).then(async confirmed => {
            if (confirmed) {
                try {
                    this.LoaderSrv.showFullScreenLoader();

                    // Call API to delete cross-section value if it has an ID
                    if (crossSectionValue.id) {
                        await this.terminalBlockApi.deleteData(crossSectionValue.id);
                    }

                    // Reload terminal block data to sync with server
                    if (this.terminalBlockForm.id) {
                        await this.loadTerminalBlockData(this.terminalBlockForm.id);
                    }

                    this.LoaderSrv.hideFullScreenLoader();
                } catch (error) {
                    console.error('Error removing cross-section value:', error);
                    this.LoaderSrv.hideFullScreenLoader();
                    // TODO: Show error message to user
                }
            }
        });
    }

    public updateTableCell(rowIndex: number, colKey: string, value: string) {
        if (this.dynamicTable.tableData[rowIndex]) {
            this.$set(this.dynamicTable.tableData[rowIndex], colKey, value);
            // Table cell data will be saved when the save button is clicked
        }
    }

    public updateCurrentValue(index: number, value: string) {
        this.$set(this.dynamicTable.currentValues[index], 'value', value);
        this.sortCurrentValues();
        this.generateTableData();
    }

    public updateCrossSectionValue(index: number, value: string) {
        this.$set(this.dynamicTable.crossSectionValues[index], 'value', value);
        this.sortCrossSectionValues();
        this.generateTableData();
    }

    private sortCurrentValues() {
        this.dynamicTable.currentValues.sort((a, b) => {
            const numA = parseFloat(a.value || '0') || 0;
            const numB = parseFloat(b.value || '0') || 0;
            return numA - numB;
        });
    }

    private sortCrossSectionValues() {
        this.dynamicTable.crossSectionValues.sort((a, b) => {
            const numA = parseFloat(a.value || '0') || 0;
            const numB = parseFloat(b.value || '0') || 0;
            return numA - numB;
        });
    }

    private generateTableData() {
        // Store existing data before regenerating
        const existingData: {[key: string]: {[key: string]: string}} = {};

        this.dynamicTable.tableData.forEach((row, rowIndex) => {
            const currentValue = this.dynamicTable.currentValues[rowIndex];
            if (currentValue && currentValue.id) {
                existingData[currentValue.id] = {...row};
            }
        });

        // Generate new table structure
        this.dynamicTable.tableData = this.dynamicTable.currentValues.map((current, index) => {
            const row: {[key: string]: string} = {
                currentValue: current.value || '',
            };

            this.dynamicTable.crossSectionValues.forEach((crossSection, crossIndex) => {
                const cellKey = `cross_${crossIndex}`;
                // Try to preserve existing data if it exists
                if (current.id && existingData[current.id] && existingData[current.id][cellKey]) {
                    row[cellKey] = existingData[current.id][cellKey];
                } else {
                    row[cellKey] = '';
                }
            });

            return row;
        });
    }

    public get tableHeaders() {
        // Since v-data-table doesn't support complex header structures like colspan/rowspan easily,
        // we'll create a custom table structure instead
        return [];
    }

    public get crossSectionHeaders() {
        return this.dynamicTable.crossSectionValues.map((value, index) => ({
            text: value.value || '',
            value: `cross_${index}`,
            index: index,
        }));
    }
}
